import { auth } from "../firebase/config";
import { signOut } from "firebase/auth";
import type { QueryClient } from "@tanstack/react-query";

export const formatCurrency = (amount?: number | string) => {
  if (!amount)
    return Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(0);
  if (typeof amount === "string") {
    amount = parseFloat(amount);
  }
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(amount);
};

export const formatCurrencyShort = (amount?: number | string): string => {
  if (!amount) return "₹0";

  const value = typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(value)) return "₹0";

  if (value >= 1e7) {
    return `₹${(value / 1e7).toFixed(1).replace(/\.0$/, "")}Cr`;
  } else if (value >= 1e5) {
    return `₹${(value / 1e5).toFixed(1).replace(/\.0$/, "")}L`;
  } else if (value >= 1e3) {
    return `₹${(value / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);
};

export const formatDate = (date: string | Date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const formatDateWithTime = (date: string | Date) => {
  if (!date) return "-";
  const formattedDate = new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const formattedTime = new Date(date).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${formattedDate} ${formattedTime}`;
};

export const formatVehicleNumber = (vehicleNumber: string) => {
  if (!vehicleNumber) return "-";
  const vehicleNumberRegex = /([A-Z]{2})(\d{2})([A-Z]{1,2})(\d{4})/;
  const match = vehicleNumber.match(vehicleNumberRegex);
  if (!match) return vehicleNumber;
  return `${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
};

export const formatTime = (time: string) => {
  if (!time) return "";
  const [hours, minutes] = time.split(":");
  const date = new Date();
  date.setHours(parseInt(hours));
  date.setMinutes(parseInt(minutes));
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatTime12Hour = (timeString: string) => {
  if (!timeString) return "";
  const [hour, minute] = timeString.split(":");
  const h = parseInt(hour, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  const formattedHour = h % 12 === 0 ? 12 : h % 12;
  return `${formattedHour}:${minute} ${ampm}`;
};

export const formatText = (text: string) => {
  if (!text || typeof text !== "string") return text;

  return text
    .replace(/[_\-]+/g, " ") // replace _ and - with space
    .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase to camel Case
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2") // ALLCAPSCase → ALLCAPS Case
    .replace(/\s+/g, " ") // collapse multiple spaces
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (match) => match.toUpperCase()); // capitalize first letter of each word
};

export const formatKms = (kms: number | string) => {
  if (!kms)
    return Intl.NumberFormat("en-IN", {
      style: "decimal",
    }).format(0);
  if (typeof kms === "string") {
    kms = parseFloat(kms);
  }
  return new Intl.NumberFormat("en-IN", {
    style: "decimal",
  }).format(kms);
};

export type TimeFilter =
  | "today"
  | "yesterday"
  | "thisWeek"
  | "last7days"
  | "lastWeek"
  | "thisMonth"
  | "lastMonth"
  | "financialYear"
  | "custom";

export type TimeFilterWithNone = TimeFilter | "";

const getTodayRange = () => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

// Get yesterday's date range
const getYesterdayRange = () => {
  const start = new Date();
  start.setDate(start.getDate() - 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setDate(end.getDate() - 1);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const getThisWeekRange = () => {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0 (Sunday) to 6 (Saturday)
  const diffToMonday = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust if Sunday
  const start = new Date(now.setDate(diffToMonday));
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const getLast7DaysRange = () => {
  const now = new Date();
  const start = new Date(now.setDate(now.getDate() - 6));
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const getLastWeekRange = () => {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

  // Calculate days to subtract to get to last Monday
  // If today is Monday (1), subtract 7 to get last Monday
  // If today is Tuesday (2), subtract 8 to get last Monday
  // If today is Sunday (0), subtract 6 to get last Monday
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek + 6;

  const start = new Date(now);
  start.setDate(now.getDate() - daysToSubtract);
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(start.getDate() + 6); // Add 6 days to get to Sunday
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

const getThisMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const getLastMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const getFinancialYearRange = () => {
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-11
  let financialYearStartYear;

  if (currentMonth >= 3) {
    // April (3) to March
    financialYearStartYear = now.getFullYear();
  } else {
    financialYearStartYear = now.getFullYear() - 1;
  }

  const start = new Date(financialYearStartYear, 3, 1); // April 1st
  start.setHours(0, 0, 0, 0);
  const end = new Date(financialYearStartYear + 1, 2, 31); // March 31st
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

export const getDateRangeForFilter = (
  reportRange: TimeFilter
): { start: Date; end: Date } => {
  switch (reportRange) {
    case "today":
      return getTodayRange();
    case "yesterday":
      return getYesterdayRange();
    case "thisWeek":
      return getThisWeekRange();
    case "last7days":
      return getLast7DaysRange();
    case "lastWeek":
      return getLastWeekRange();
    case "thisMonth":
      return getThisMonthRange();
    case "lastMonth":
      return getLastMonthRange();
    case "financialYear":
      return getFinancialYearRange();
    default: // Default to today
      return getTodayRange();
  }
};

export const formatDateRange = (start: Date, end?: Date): string => {
  if (!end) {
    return `(${formatDate(start)})`;
  }
  if (start.getTime() === end.getTime()) {
    return `(${formatDate(start)})`;
  }
  return `(${formatDate(start)} - ${formatDate(end)})`;
};

export const formatToLocalYMD = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const handleLogout = async (queryClient?: QueryClient) => {
  try {
    await signOut(auth);
    sessionStorage.clear();
    localStorage.clear();
    if (queryClient) {
      queryClient.invalidateQueries({ queryKey: ["planStatus"] });
    }
    window.location.href = "/";
  } catch (error) {}
};

export const formatInputCurrency = (value: any) => {
  if (value == null) return "";
  const stringValue = String(value);
  const cleanValue = stringValue.replace(/[^0-9.]/g, "");
  const [intPart, decPart] = cleanValue.split(".");
  const formattedInt = parseInt(intPart || "0", 10).toLocaleString("en-IN");

  let formattedValue = formattedInt;
  if (decPart !== undefined) {
    formattedValue += "." + decPart.slice(0, 2); // Keep max 2 decimals
  }

  return formattedValue;
};
